import { useMemo } from 'react';

/**
 * The stable request-params object a list query hook hands React Query.
 *
 * It does two things at once, and the second is the one that matters:
 *
 *  1. Drops `undefined` fields, so `{ search: 'x', tags: undefined }` and
 *     `{ search: 'x' }` are the same request rather than two query keys.
 *  2. Keys the memo on a CONTENT signature over every remaining field, so a
 *     filter cannot fail to participate.
 *
 * (2) replaces the shape this repo had at 17 call sites — `useMemo(() =>
 * cleanParams(params), [params?.search, params?.status, …])` behind an
 * `eslint-disable react-hooks/exhaustive-deps`. A hand-listed dep array over an
 * object the factory reads WHOLE is exhaustive only until the next filter is
 * added, and the failure is silent in the worst way: the params object never
 * changes, so the query key never changes, so React Query issues no request —
 * no skeleton, no error, the previous rows still on screen under a chip and a
 * URL that both say a filter is active. `staleness` was missing from the
 * contacts and organizations lists, so the whole Staleness select was inert:
 * 25 rows live against 0 rows on a reload of the identical URL (WI 7800e951).
 *
 * Deriving the dependency from the content makes that structurally impossible —
 * a filter cannot be forgotten, because no filter is named.
 *
 * @example
 * const stableParams = useStableListParams(params);
 * return useQuery({
 *   queryKey: thingKeys.list(stableParams),
 *   queryFn: () => thingApi.list(stableParams),
 * });
 */

/**
 * A deterministic signature over a params object. Keys are sorted, so the order
 * the caller happened to spell its object literal in never counts as a change.
 *
 * Query params are strings, numbers, booleans and arrays of those, so JSON is a
 * complete encoding of them; `null` stands in for an absent params object,
 * which `JSON.stringify` would otherwise render as `undefined`.
 */
function paramsSignature(value: unknown): string {
  return JSON.stringify(value ?? null, (_key, val) =>
    val !== null && typeof val === 'object' && !Array.isArray(val)
      ? Object.fromEntries(
          Object.entries(val as Record<string, unknown>).sort(([a], [b]) =>
            a < b ? -1 : a > b ? 1 : 0,
          ),
        )
      : val,
  );
}

/** Drop `undefined` fields; an object left with none is no params at all. */
function cleanParams<T extends object>(params?: T): T | undefined {
  if (!params) return undefined;
  const cleaned = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined),
  ) as T;
  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
}

export function useStableListParams<T extends object>(params?: T): T | undefined {
  const cleaned = cleanParams(params);
  const signature = paramsSignature(cleaned);

  // `signature` IS the dependency: it is a content encoding of every field of
  // `cleaned`, so this dep array is exhaustive by construction. Listing
  // `cleaned` instead would defeat the memo (a fresh object every render),
  // which is the whole point. This is the ONE suppression the class needs —
  // the seventeen it replaces each stood for a hand-picked field list.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => cleaned, [signature]);
}
